import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2"; // ✅ Added missing import
import RichTextEditor from "../../../components/common/RichTextEditor"; // adjust path
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import { ToastContainer } from "react-toastify";

const API_BASE = "https://tyka.premierhostings.com/beta/api";
const bannerTypes = ["main"];
const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE = MAX_VIDEO_SIZE_MB * 1024 * 1024;

export default function EditHeadCategoryPage() {
  const { id } = useParams();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false); // ✅ Loader state added
  const [categoryFile, setCategoryFile] = useState(null);
  const [categoryPreview, setCategoryPreview] = useState("");
  const [categoryType, setCategoryType] = useState("");
  const [categoryImagePreview, setCategoryImagePreview] = useState("");
  const [videoPreview, setVideoPreview] = useState("");
  const [banners, setBanners] = useState([]);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Load existing category data
  useEffect(() => {
    if (!id) return;

    axios
      .get(`${API_BASE}/product-categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data.data;

        setName(data.name || "");
        setSlug(data.slug || "");
        setDescription(data.description || "");

        if (data.category_image) {
          setCategoryImagePreview(data.category_image);
          setCategoryType("image");
        }

        if (data.video) {
          setVideoPreview(data.video);
          setCategoryType("video");
        }

        setBanners(
          (data.banners || []).map((banner) => ({
            id: banner.id,
            type: banner.banner_type,
            image: null,
            preview: banner.image,
          })),
        );
      })
      .catch((err) => {
        toast.error("Failed to load category");
        console.error(err.response?.data || err.message);
      });
  }, [id, token]);

  // Auto-generate slug from name, but allow manual edits as well
  useEffect(() => {
    if (name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");
      setSlug(generatedSlug);
    } else {
      setSlug("");
    }
  }, [name]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const namePattern = /^[A-Za-z\s-]+$/;

    if (!namePattern.test(name.trim())) {
      return toast.error(
        "Category name can only contain letters, spaces, and hyphens.",
      );
    }

    if (!name.trim()) {
      return toast.error("Please enter a category name");
    }

    if (!slug.trim()) {
      return toast.error("Slug cannot be empty.");
    }

    if (!token) {
      return toast.error("No token found. Please login again.");
    }

    try {
      setLoading(true);

      Swal.fire({
        title: "Updating Category...",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const formData = new FormData();

      formData.append("name", name);
      formData.append("slug", slug);
      formData.append("description", description || "");
      formData.append("category_id", "");

      // Image / Video
      if (categoryFile) {
        if (categoryFile.type.startsWith("image/")) {
          formData.append("category_image", categoryFile);
        } else {
          formData.append("video", categoryFile);
        }
      }

      // Banners
      const bannersData = [];

      banners.forEach((banner, index) => {
        bannersData.push({
          id: banner.id || "",
          type: banner.type,
          file_index: banner.image instanceof File ? index : null,
        });

        if (banner.image instanceof File) {
          formData.append(`banners[${index}][image]`, banner.image);
        }
      });

      // Always send banners as an array, even when empty
      formData.append("banners", JSON.stringify(bannersData));

      // Laravel PUT with multipart/form-data
      formData.append("_method", "PUT");

      // Debug
      for (const [key, value] of formData.entries()) {
        console.log(key, value);
      }

      const response = await axios.post(
        `${API_BASE}/product-categories/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.close();

      toast.success(response.data.message || "Category updated successfully!");

      setTimeout(() => {
        navigate("/headcategory");
      }, 800);
    } catch (error) {
      Swal.close();

      console.error(error.response?.data);

      if (error.response?.status === 422) {
        const errors = error.response.data.errors;

        if (errors) {
          Object.values(errors).forEach((messages) => {
            messages.forEach((msg) => toast.error(msg));
          });
        } else {
          toast.error(error.response.data.message);
        }
      } else {
        toast.error(error.response?.data?.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleCategoryImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      toast.error("Please select a valid image or video file.");
      e.target.value = "";
      return;
    }
    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      toast.error(`Video size must be ${MAX_VIDEO_SIZE_MB}MB or less.`);
      e.target.value = "";
      return;
    }

    // Revoke old local previews to avoid leaking memory
    if (categoryImagePreview?.startsWith?.("blob:")) {
      URL.revokeObjectURL(categoryImagePreview);
    }
    if (videoPreview?.startsWith?.("blob:")) {
      URL.revokeObjectURL(videoPreview);
    }

    const previewUrl = URL.createObjectURL(file);

    setCategoryFile(file);

    if (isVideo) {
      setVideoPreview(previewUrl);
      setCategoryImagePreview("");
      setCategoryType("video");
    } else {
      setCategoryImagePreview(previewUrl);
      setVideoPreview("");
      setCategoryType("image");
    }
  };

  const handleBannerTypeChange = (index, value) => {
    const updated = [...banners];
    updated[index].type = value;
    setBanners(updated);
  };

  const handleBannerImageChange = (index, file) => {
    if (!file) return;

    const updated = [...banners];

    updated[index].image = file;
    updated[index].preview = URL.createObjectURL(file);

    setBanners(updated);
  };

  const addBanner = () => {
    setBanners([
      ...banners,
      {
        type: "main",
        image: null,
        preview: "",
      },
    ]);
  };

  const removeBanner = (index) => {
    setBanners(banners.filter((_, i) => i !== index));
  };

  const handleDeleteCategoryMedia = async () => {
    const resetLocalMedia = () => {
      if (categoryImagePreview?.startsWith?.("blob:")) {
        URL.revokeObjectURL(categoryImagePreview);
      }
      if (videoPreview?.startsWith?.("blob:")) {
        URL.revokeObjectURL(videoPreview);
      }

      setCategoryFile(null);
      setCategoryImagePreview("");
      setVideoPreview("");
      setCategoryType("");

      const fileInput = document.getElementById("category-media-input");
      if (fileInput) fileInput.value = "";
    };

    const currentPreview =
      categoryType === "video" ? videoPreview : categoryImagePreview;

    // Newly picked file, not saved on the server yet: clear locally only
    if (currentPreview?.startsWith?.("blob:")) {
      resetLocalMedia();
      return;
    }

    const result = await Swal.fire({
      title: `Delete ${categoryType}?`,
      text: "This will permanently remove it.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: "Deleting...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      // TODO: replace with your real category delete route
      const DELETE_URL = `${API_BASE}/categories/${id}/${
        categoryType === "video" ? "video" : "image"
      }`;

      await axios.delete(DELETE_URL);

      Swal.close();
      resetLocalMedia();
      toast.success("Deleted successfully!");
    } catch (error) {
      Swal.close();
      toast.error(error.response?.data?.message || "Failed to delete.");
      console.error("Delete media error:", error.response?.data || error);
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold mb-5 dark:text-white">
          Edit Main Category
        </h1>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block mb-1 font-medium dark:text-gray-300"
            >
              Name
            </label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter category name"
            />
          </div>

          {/* Slug */}
          <div>
            <label
              htmlFor="slug"
              className="block mb-1 font-medium dark:text-gray-300"
            >
              Slug
            </label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Auto-generated slug"
              readOnly
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="block mb-1 font-medium dark:text-gray-300"
            >
              Description
            </label>
            <RichTextEditor
              value={description}
              initialContent={description}
              onChange={setDescription}
            />
          </div>

          <div className="w-full">
            <label className="block mb-2 font-medium">Category Video</label>

            <input
              id="category-media-input"
              type="file"
              accept="image/*,.mp4,.mov,.avi,.mkv,.webm"
              onChange={handleCategoryImage}
              className="block w-full"
            />

            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              You can upload either one image or one video. Maximum video size
              is {MAX_VIDEO_SIZE_MB}MB.
            </p>

            {categoryType === "image" && categoryImagePreview && (
              <div className="mt-3 flex w-full flex-col gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCategoryMedia}
                  className="self-end rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                >
                  Delete Image
                </button>

                <img
                  src={categoryImagePreview}
                  className="h-60 w-full rounded object-cover"
                  alt="Category preview"
                />
              </div>
            )}

            {categoryType === "video" && videoPreview && (
              <div className="mt-3 flex w-full flex-col gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCategoryMedia}
                  className="self-end shrink-0 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                >
                  Delete Video
                </button>

                <video
                  src={videoPreview}
                  controls
                  className="h-60 w-full rounded object-cover"
                />
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div className="flex justify-between">
              <h3 className="font-semibold text-lg">Banners</h3>

              <Button type="button" onClick={addBanner}>
                + Add Banner
              </Button>
            </div>

            {banners.map((banner, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4">
                <h4 className="text-lg font-semibold text-gray-700 dark:text-white">
                  Banner {index + 1}
                </h4>

                {/* Banner Type */}
                <div>
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Banner Type
                  </label>

                  <select
                    value={banner.type}
                    onChange={(e) =>
                      handleBannerTypeChange(index, e.target.value)
                    }
                    className="w-full border rounded-lg p-2"
                  >
                    {bannerTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Banner Image */}
                <div>
                  <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
                    Banner Image
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleBannerImageChange(index, e.target.files[0])
                    }
                    className="block w-full border rounded-lg p-2"
                  />

                  {banner.preview && (
                    <img
                      src={banner.preview}
                      alt={`Banner ${index + 1}`}
                      className="mt-3 h-full w-full rounded border object-contain"
                    />
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={() => removeBanner(index)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/headcategory")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Category"}
            </Button>
          </div>
        </form>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
}
